import React, {useState} from 'react';
import {toast} from 'react-toastify';
import {AxiosError} from 'axios';
import {useTheme} from '@emotion/react';

// helpers
import {useScreenWidth} from '../../../../shared/hooks';
import {CreditBookApi} from '../../../../shared/services/ApiService';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

// components
import {
  CardBorder,
  CardPaymentLoan,
  ConcurrencyResolutionModal,
  DeferModal,
  TablePaymentsLoan,
} from '../../../../shared/components';

// styles
import {Subtitle, TextColors} from '../../../../shared/styles';
import {
  SectionPaymentsDesktopWrapper,
  SectionPaymentsMobileWrapper,
} from './SectionPayments-styles';

// types
import {
  LoanPayment,
  LoanPaymentDeferralPayload,
} from '../../../../shared/services/ApiService/types';

type Props = {
  payments: LoanPayment[];
  isVariableCredit: boolean | undefined;
};

/**
 * Post payment deferral
 * @param paymentPayload
 */
const postPaymentDeferral = async (
  paymentPayload: LoanPaymentDeferralPayload,
) => {
  const response =
    await CreditBookApi.loan.postLoanPaymentDeferral(paymentPayload);
  return response.data;
};

const SectionPayments = ({payments, isVariableCredit}: Props) => {
  const {t} = useTranslation();
  const screenWidth = useScreenWidth();
  const theme = useTheme();
  const queryClient = useQueryClient();

  const [isDeferModalOpen, setIsDeferModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [paymentToDefer, setPaymentToDefer] = useState<LoanPayment | null>(
    null,
  );
  const [updateMessage, setUpdateMessage] = useState('');

  /**
   * On Defer Payment Click opens DeferModal
   * @param e
   */
  const onDeferPaymentClick = (e: React.MouseEvent, payment: LoanPayment) => {
    e.stopPropagation();
    setIsDeferModalOpen(true);
    setPaymentToDefer(payment);
  };

  /**
   * On close function for Defer Modal
   * Close the DeferModal
   */
  const onDeferModalClose = () => {
    setIsDeferModalOpen(false);
    setPaymentToDefer(null);
  };

  /**
   * On confirm function for Defer Modal
   * Close the DeferModal and call the API to defer the payment
   */
  const onDeferModalConfirm = () => {
    if (paymentToDefer != null) {
      requestPaymentDeferral({
        paymentId: paymentToDefer.id,
        loanGuidLocker: paymentToDefer.loanGuidLocker,
      });
    }
    setIsDeferModalOpen(false);
    setPaymentToDefer(null);
  };

  /**
   * Success handler by displaying a toast and refetching the queries
   */
  const handleOnSuccess = () => {
    toast.success(t('paymentDeferral_success'));
    queryClient.refetchQueries();
  };

  /**
   * Error handler by showing a toast
   * @param err
   */
  const handleOnError = (
    err: AxiosError<{errorCode: string; errorMessage: string}>,
  ) => {
    const errorString =
      err?.response?.data.errorMessage ?? t('global_errorOccurred');
    if (err.status === 409) {
      setIsUpdateModalOpen(true);
      setUpdateMessage(errorString);
    } else {
      toast.error(errorString);
    }
  };

  // Request new loan
  const {mutate: requestPaymentDeferral} = useMutation<
    {message: string},
    AxiosError<{errorCode: string; errorMessage: string}>,
    LoanPaymentDeferralPayload
  >({
    mutationFn: postPaymentDeferral,
    onSuccess: handleOnSuccess,
    onError: (err) => handleOnError(err),
  });

  const renderSectionPayments = () => {
    // Extract integer part from theme breakpoint
    const screenBreakpoint = theme.screenBreakpoint.md.value;
    if (screenWidth < screenBreakpoint) {
      return (
        <SectionPaymentsMobileWrapper>
          <Subtitle bold="bold" color={TextColors.secondary}>
            {t('loan_payments')}
          </Subtitle>
          {payments.map((payment) => (
            <CardPaymentLoan
              key={payment.id}
              payment={payment}
              onDeferPaymentClick={onDeferPaymentClick}
            />
          ))}
        </SectionPaymentsMobileWrapper>
      );
    }

    return (
      <CardBorder variant="white">
        <SectionPaymentsDesktopWrapper>
          <Subtitle bold="bold">{t('loan_payments')}</Subtitle>
          <TablePaymentsLoan
            payments={payments}
            isVariableCredit={isVariableCredit}
            onDeferPaymentClick={onDeferPaymentClick}
          />
        </SectionPaymentsDesktopWrapper>
      </CardBorder>
    );
  };

  return (
    <>
      <DeferModal
        isOpen={isDeferModalOpen}
        onCloseCallBack={onDeferModalClose}
        onConfirmCallback={onDeferModalConfirm}
      />
      <ConcurrencyResolutionModal
        isOpen={isUpdateModalOpen}
        message={updateMessage}
      />
      {renderSectionPayments()}
    </>
  );
};

export default SectionPayments;
